import React, { useState } from 'react';
import MaterialTable from 'material-table';
import useAxios from 'axios-hooks';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import PrintIcon from '@material-ui/icons/Print';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

import { IOrder, IOrderState } from '../../types';
import localization from '../../helpers/material-table/localization';
import OrderStateModal from '../../components/OrderStateModal';
import { axiosClient } from '../../helpers/AxiosClient';
import { OrderProductsDetail } from './OrderProductsDetail';

const columns = [
  {
    title: 'Stato',
    field: 'state',
    render: (order: IOrder) => (
      <FiberManualRecordIcon
        htmlColor={
          order.state === IOrderState.new ? '#6efa5a' : order.state === IOrderState.delivering ? '#fad343' : '#0048ff'
        }
      />
    ),
    width: 1,
  },
  { title: 'Data', render: (order: IOrder) => moment(new Date(moment(order.date).format('YYYY-MM-DD'))).format('D/M') },
  {
    title: 'Nome',
    render: ({ name, surname }: IOrder) => (
      <div>
        {name}
        <br />
        {surname}
      </div>
    ),
  },
  {
    title: 'Contatti',
    render: ({ user }: IOrder) => (
      <div>
        {user?.phone}
        <br />
        {user?.email}
      </div>
    ),
  },
  { title: 'Indirizzo', field: 'address' },
  { title: 'Città', field: 'city' },
  {
    title: 'Data consegna',
    render: ({ deliveryDate }: IOrder) => (deliveryDate ? moment(deliveryDate).format('ddd D MMM') : ''),
    grouping: true,
  },
  {
    title: 'Prodotti',
    field: 'productsWithQuantity',
    sorting: false,
    grouping: false,
    render: (order: IOrder) => <OrderProductsDetail order={order} />,
  },
];

const Orders: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [filterStates, setFilterStates] = useState<Set<IOrderState>>(
    new Set([IOrderState.new, IOrderState.delivering])
  );
  const [{ data: orders = [], loading, error }, refreshOrders] = useAxios<IOrder[]>(
    {
      url: `orders`,
      params: {
        filters: { state: Array.from(filterStates) },
      },
    },
    {
      useCache: false,
    }
  );
  const [updatingOrder, setUpdatingOrder] = useState<IOrder | null>(null);

  if (error) {
    enqueueSnackbar(error.message, { variant: 'error' });
  }

  const cleanOrders: IOrder[] = orders.map((order) => ({
    ...order,
  }));

  const handleFilterStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const orderState = event.target.name as IOrderState;

    if (filterStates.has(orderState)) {
      filterStates.delete(orderState);
    } else {
      filterStates.add(orderState);
    }

    setFilterStates(new Set(filterStates));
  };

  const modalCanceled = () => {
    setUpdatingOrder(null);
  };

  const modalConfirmed = (state: IOrderState) => {
    axiosClient
      .put(`orders/${updatingOrder?.id}`, {
        data: {
          ...updatingOrder,
          state,
        },
      })
      .then(() => {
        refreshOrders().catch(console.error);
      });
  };

  const handleOrderStateClick = (event: any, rowData: IOrder | IOrder[]) => {
    const orders: IOrder[] = rowData instanceof Array ? rowData : [rowData];
    setUpdatingOrder(orders[0]);
  };

  const handleOrderPrintClick = (event: any, rowData: IOrder | IOrder[]) => {
    const order: IOrder = rowData instanceof Array ? rowData[0] : rowData;
    axiosClient
      .get(`orders/${order.id}/print`)
      .then((response) => {
        const byteCharacters = atob(response.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch((reason) => {
        console.error(reason);
      });
  };

  return (
    <Container>
      <Typography variant="h3" gutterBottom color="primary">
        Ordini
      </Typography>
      <FormControl component="fieldset">
        <FormLabel component="legend">Filtra stato ordine</FormLabel>
        <FormGroup style={{ flexDirection: 'row' }}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={filterStates.has(IOrderState.new)}
                onChange={handleFilterStateChange}
                name={IOrderState.new}
              />
            }
            label="Nuovo"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={filterStates.has(IOrderState.delivering)}
                onChange={handleFilterStateChange}
                name={IOrderState.delivering}
              />
            }
            label="In consegna"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={filterStates.has(IOrderState.completed)}
                onChange={handleFilterStateChange}
                name={IOrderState.completed}
              />
            }
            label="Completato"
          />
        </FormGroup>
      </FormControl>
      <MaterialTable
        title={''}
        localization={localization}
        columns={columns}
        data={cleanOrders}
        isLoading={loading}
        options={{
          grouping: true,
          actionsColumnIndex: -1,
          pageSize: 25,
          pageSizeOptions: [25],
        }}
        actions={[
          {
            icon: () => <MoreHorizIcon />,
            tooltip: 'Modifica stato',
            onClick: handleOrderStateClick,
          },
          {
            icon: () => <PrintIcon />,
            tooltip: 'Stampa',
            onClick: handleOrderPrintClick,
          },
          {
            icon: 'refresh',
            tooltip: 'Aggiorna',
            isFreeAction: true,
            onClick: () => refreshOrders(),
          },
        ]}
      />
      <OrderStateModal order={updatingOrder} onUpdate={modalConfirmed} onCancel={modalCanceled} />
    </Container>
  );
};

export default Orders;
